import { useState } from 'react';
import { Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { toast } from 'sonner';
import { authService, userService } from '../lib/databaseService';

export const UserAuthModal = ({ isOpen, onClose, onLogin, users = [], onUpdateUsers, agencies = [] }) => {
  // Ensure users and agencies are always arrays
  const safeUsers = Array.isArray(users) ? users : [];
  const safeAgencies = Array.isArray(agencies) ? agencies : [];
  const [activeTab, setActiveTab] = useState('login');
  
  // SMS/OTP form
  const [otpPhone, setOtpPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  
  const handleRegister = (e) => {
    e.preventDefault();
    toast.info('Qeydiyyat Google hesabı ilə aparılır. Lütfən yuxarıdakı düymədən istifadə edin.');
  };

  // Google ile giriş
  const handleGoogleLogin = async () => {
    try {
      await authService.signInWithGoogle();
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Google ilə giriş zamanı xəta baş verdi');
    }
  };

  // SMS/OTP gönderme
  const handleSendOTP = async () => {
    if (!otpPhone) {
      toast.error('Telefon nömrəsi daxil edin');
      return;
    }

    setIsSendingOtp(true);
    try {
      await authService.sendOTP(otpPhone);
      setIsOtpSent(true);
      toast.success('OTP kodu telefonunuza göndərildi');
    } catch (error) {
      console.error('Send OTP error:', error);
      toast.error('OTP göndərilmədi. Zəhmət olmasa yenidən cəhd edin.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  // OTP doğrulama
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otpCode || otpCode.length !== 6) {
      toast.error('6 rəqəmli OTP kodunu daxil edin');
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const result = await authService.verifyOTP(otpPhone, otpCode);
      
      if (result?.user) {
        // Kullanıcıyı veritabanında bul veya oluştur
        let user = await userService.findByPhone(otpPhone);
        
        if (!user) {
          // Yeni kullanıcı oluştur
          const newUser = {
            name: result.user.user_metadata?.full_name?.split(' ')[0] || 'İstifadəçi',
            surname: result.user.user_metadata?.full_name?.split(' ').slice(1).join(' ') || '',
            email: result.user.email || `phone-${otpPhone}@mydom.az`,
            phone: otpPhone,
            password: '',
            isAgent: false,
          };
          
          user = await userService.register(newUser);
        }

        const userData = {
          id: user.id,
          name: user.name,
          surname: user.surname || '',
          email: user.email,
          phone: user.phone || otpPhone,
          agencyId: user.agency_id,
          isAgent: user.is_agent || false,
        };

        onLogin(userData);
        toast.success(`Xoş gəldiniz, ${user.name}!`);
        onClose();
        resetForm();
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      toast.error('OTP kodu yanlışdır və ya vaxtı bitib');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const resetForm = () => {
    setOtpPhone('');
    setOtpCode('');
    setIsOtpSent(false);
    setActiveTab('login');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { onClose(); resetForm(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="user-auth-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold logo-text text-center">
            İstifadəçi Hesabı
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Giriş</TabsTrigger>
            <TabsTrigger value="register">Qeydiyyat</TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="space-y-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google ilə davam et
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">və ya</span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">və ya</span>
              </div>
            </div>
            <div className="space-y-3">
              {!isOtpSent ? (
                <>
                  <div className="space-y-2">
                    <Label>Telefon Nömrəsi</Label>
                    <div className="relative">
                      <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        type="tel"
                        value={otpPhone}
                        onChange={(e) => setOtpPhone(e.target.value)}
                        placeholder="+994 XX XXX XX XX"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSendOTP}
                    className="w-full"
                    disabled={isSendingOtp || !otpPhone}
                  >
                    {isSendingOtp ? 'Göndərilir...' : 'SMS/OTP ilə giriş'}
                  </Button>
                </>
              ) : (
                <form onSubmit={handleVerifyOTP} className="space-y-3">
                  <div className="space-y-2">
                    <Label>OTP Kodu (6 rəqəm)</Label>
                    <InputOTP
                      maxLength={6}
                      value={otpCode}
                      onChange={(value) => setOtpCode(value)}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                    <p className="text-xs text-gray-500 text-center">
                      {otpPhone} nömrəsinə göndərilən kodu daxil edin
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsOtpSent(false);
                        setOtpCode('');
                      }}
                      className="flex-1"
                    >
                      Geri
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-[#B91C1C] hover:bg-[#991B1B]"
                      disabled={isVerifyingOtp || otpCode.length !== 6}
                    >
                      {isVerifyingOtp ? 'Yoxlanılır...' : 'Təsdiq et'}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </TabsContent>

          <TabsContent value="register" className="space-y-4 pt-4">
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Ad</Label>
                  <Input
                    placeholder="Adınız"
                    data-testid="reg-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Soyad</Label>
                  <Input
                    placeholder="Soyadınız"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  placeholder="+994 XX XXX XX XX"
                  disabled
                />
              </div>
              <Button type="submit" className="w-full bg-[#B91C1C] hover:bg-[#991B1B]">
                Qeydiyyatdan keç
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
